import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { MONTHS, fmtSEK, g } from "../revenue/format";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../ui/sheet";

const MONTH_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-0.5">{children}</div>
    </div>
  );
}

export default function IndirectDetailPanel({ open, onClose, account, month, data, year, canEdit, onApplyForward }) {
  const [m, setM] = useState(month);
  const [amount, setAmount] = useState("");

  useEffect(() => {
    setM(month);
    setAmount("");
  }, [month, account?.id, open]);

  if (!account || !data) {
    return <Sheet open={open} onOpenChange={(v) => !v && onClose()} />;
  }

  const cell = g(account.months, m) || {};
  const period = g(data.periods, m) || {};
  const closed = period.status === "CLOSED";
  const isImportedActual = cell.actual_source === "IMPORTED_ACTUAL";
  const editable = canEdit && !account.system && !closed && !isImportedActual;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto" data-testid="ic-detail-panel">
        <SheetHeader>
          <SheetTitle className="font-heading">
            {account.account_number} {account.description || ""}
          </SheetTitle>
          <SheetDescription>
            {data.company_name} — {year}. Why is this number what it is?
          </SheetDescription>
        </SheetHeader>

        {account.system && (
          <div className="mt-4 bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2" data-testid="ic-panel-system-note">
            <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
            <p className="text-xs text-slate-600">
              Account 6591 is reserved for Group Overhead. It will be calculated automatically in the Group build (Build 8) and is not manually editable. Until then no amount is calculated.
            </p>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-1" data-testid="ic-panel-months">
          {MONTHS.map((label, i) => {
            const mm = i + 1;
            const p = g(data.periods, mm) || {};
            return (
              <button
                key={mm}
                onClick={() => setM(mm)}
                className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                  m === mm
                    ? "bg-[#0052CC] text-white border-[#0052CC]"
                    : p.status === "CLOSED"
                      ? "bg-slate-100 text-slate-500 border-slate-200 hover:border-slate-300"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`ic-panel-month-${mm}`}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="mt-4 border border-slate-200 rounded-lg p-4 grid grid-cols-2 gap-4" data-testid="ic-panel-detail">
          <Field label="Period" testId="ic-panel-period">
            {MONTH_FULL[m - 1]} {year}
          </Field>
          <Field label="Period status" testId="ic-panel-status">
            {closed ? `Closed · ${period.actual_status === "VERIFIED_ACTUAL" ? "Verified actual" : period.actual_status || "—"}` : "Open · forecast"}
          </Field>
          <Field label={isImportedActual ? "Preserved forecast" : "Forecast amount"} testId="ic-panel-forecast">
            {cell.forecast_amount == null ? "—" : `${fmtSEK(cell.forecast_amount)} kr`}
            {cell.forecast_amount != null && <span className="text-[10px] font-semibold text-slate-400 uppercase ml-1.5">INPUT</span>}
          </Field>
          <Field label="Actual amount" testId="ic-panel-actual">
            {cell.actual_amount == null ? "—" : `${fmtSEK(cell.actual_amount)} kr`}
            {cell.actual_amount != null && (
              <span className="text-[10px] font-semibold text-slate-400 uppercase ml-1.5">
                {cell.actual_source || "ACTUAL"}{cell.verified ? " · verified" : ""}
              </span>
            )}
          </Field>
          <Field label="Active value (One Number)" testId="ic-panel-active-value">
            {cell.value == null ? (closed ? "Actual unavailable" : "—") : `${fmtSEK(cell.value)} kr`}
          </Field>
          <Field label="Cell state" testId="ic-panel-cell-state">
            {isImportedActual
              ? "Accounting actual — read-only (committed import)"
              : account.system ? "System — read-only" : closed ? "Locked — closed period" : "Editable forecast"}
          </Field>
          <Field label="Payment terms assumption" testId="ic-panel-terms">
            {account.system
              ? "Set by Group build"
              : account.payment_terms
                ? `${account.payment_terms.name} (${account.payment_terms.days} days)`
                : <span className="text-[#B45309]">Missing — required for Build 7 cash timing</span>}
          </Field>
          <Field label="VAT assumption" testId="ic-panel-vat">
            {account.system
              ? "Set by Group build"
              : account.vat_rate
                ? `${account.vat_rate.name} (${account.vat_rate.rate}%)`
                : <span className="text-[#B45309]">Missing — required for Build 7 VAT handling</span>}
          </Field>
          <Field label="Last updated" testId="ic-panel-updated">
            {cell.updated_at
              ? `${cell.updated_by_name || "—"} · ${String(cell.updated_at).slice(0, 10)}`
              : "—"}
          </Field>
          <Field label="Provenance" testId="ic-panel-provenance">
            {isImportedActual ? "IMPORTED_ACTUAL" : account.system ? "CALCULATED (future)" : cell.source || "—"}
          </Field>
          {isImportedActual && (
            <Field label="Source" testId="ic-panel-source">
              Accounting P&L Import{cell.actual_file_name ? ` — ${cell.actual_file_name}` : ""}
              {cell.actual_batch_id && (
                <span className="block text-[10px] text-slate-400 font-normal">batch {cell.actual_batch_id.slice(0, 8)}…</span>
              )}
            </Field>
          )}
        </div>

        {editable && (
          <div className="mt-4 border border-slate-200 rounded-lg p-4 space-y-3" data-testid="ic-apply-forward">
            <Label>Apply a forecast amount to {MONTH_FULL[m - 1]}–December (open months only)</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={amount}
                placeholder={cell.forecast_amount != null ? String(cell.forecast_amount) : "Amount"}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="ic-apply-forward-amount"
              />
              <Button
                className="bg-[#0052CC] hover:bg-[#0047B3] whitespace-nowrap"
                disabled={amount === "" || Number.isNaN(Number(amount))}
                onClick={() => onApplyForward(account.id, m, Number(amount))}
                data-testid="ic-apply-forward-button"
              >
                Apply forward
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Closed months are never overwritten. Every resulting value stays an explicit INPUT forecast — no hidden recurrence.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
